import React, { useEffect, useRef, useState, ReactElement } from 'react';
import classnames from 'classnames';
import styles from './DropdownFilter.module.scss';
import { DocumentNode } from '@apollo/client';
import DropdownFilterItems from './DropdownFilterItems';
import { ApiClients } from 'constants/apiClients';
import { ColorNames } from 'constants/colorNames';
import { IdName } from 'interfaces/IdName';
import Icon from 'components/Icon';
import { colors } from 'constants/colors';
import { useTranslation } from 'react-i18next';
import { I18N_PLATFORM_COMMON_WORD_PATH } from 'constants/i18n';

export type Props = {
  borderColor?: ColorNames;
  clientSideFilter?: (searchedText: string, items: any[]) => any[];
  fullWidth?: boolean;
  graphqlClient?: ApiClients;
  graphqlQuery: DocumentNode;
  graphqlVariables?: (searchText?: string) => {};
  groupBySubfield?: string;
  isDisabled?: boolean;
  isCustomKeyName?: boolean;
  isFilterActive?: boolean;
  isMultipleSelect?: boolean;
  isRequired?: boolean;
  skipQuery?: boolean;
  itemRenderer?: (item: any, searchedText?: string) => ReactElement;
  label: string;
  labelClassName?: string;
  mapper: (queryResultData: any) => any[];
  noBorderOnValueSelected?: boolean;
  onChange: (newValue: any, data?: any) => void;
  placeholder?: string;
  renderFooterSelection?: boolean;
  selectedValue?: any;
  verticalLabel?: string;
  wrapperClassName?: string;
  itemsClassName?: string;
  shouldShowInput?: boolean;
  selectAllOption?: {
    value: string;
    isVisible: boolean;
    selectAllByDefault?: (allItems: IdName[]) => void;
  };
  actionButton?: {
    value: string;
    isVisible: boolean;
    isDisabled: boolean;
    onClick?: () => void;
  };
  optionsWrapperClassName?: string;
  showSelectAndDeselect?: boolean;
  customFooterComponent?: React.ReactNode;
  labelFieldName?: string;
};

const DropdownFilter: React.FC<Props> = ({
  label,
  mapper,
  onChange,
  fullWidth,
  isDisabled,
  borderColor,
  placeholder,
  graphqlQuery,
  itemRenderer,
  actionButton,
  graphqlClient,
  selectedValue,
  verticalLabel,
  isFilterActive,
  isCustomKeyName,
  groupBySubfield,
  shouldShowInput,
  selectAllOption,
  clientSideFilter,
  graphqlVariables,
  isMultipleSelect,
  wrapperClassName,
  itemsClassName,
  labelClassName,
  renderFooterSelection,
  noBorderOnValueSelected = false,
  isRequired,
  skipQuery,
  optionsWrapperClassName,
  customFooterComponent,
  labelFieldName,
}) => {
  const [isActive, setIsActive] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  const iconName = isActive ? 'arrowUp' : 'arrowDown';
  const iconColor = isDisabled ? colors.ayGrey64Color : colors.primaryColor500;

  const hasValue =
    (!isMultipleSelect && !!selectedValue) ||
    (isMultipleSelect && !!selectedValue?.length);
  const borderColorClass =
    styles[`border-${borderColor || ColorNames.supportiveColor500}`];

  useEffect(() => {
    if (isFilterActive) {
      setIsActive(isFilterActive);
    }
  }, [isFilterActive]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        resultsContainerRef.current &&
        filterButtonRef.current &&
        !resultsContainerRef.current.contains(event.target) &&
        !filterButtonRef.current.contains(event.target)
      ) {
        setIsActive(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [resultsContainerRef, filterButtonRef]);

  return (
    <>
      {verticalLabel && (
        <p className={styles['vertical-label']}>
          {verticalLabel}
          {isRequired && (
            <span role="note">
              {' '}
              {`(${t(`${I18N_PLATFORM_COMMON_WORD_PATH}.required`)})`}
            </span>
          )}
        </p>
      )}
      <div
        className={classnames(styles.container, {
          [styles['full-width']]: fullWidth,
        })}
      >
        <button
          type="button"
          ref={filterButtonRef}
          disabled={isDisabled}
          className={classnames(
            styles.button,
            borderColorClass,
            wrapperClassName,
            {
              [styles.active]: isActive,
              [styles['with-value-selected']]:
                !noBorderOnValueSelected && hasValue,
              [styles.disabled]: isDisabled,
              [styles['full-width']]: fullWidth,
            },
          )}
          onClick={e => {
            if (isActive) e.currentTarget.blur();
            !isDisabled && setIsActive(currentValue => !currentValue);
          }}
        >
          {<span className={labelClassName}>{label}</span>}
          <Icon
            name={iconName}
            color={iconColor}
            size={0.8}
            className={styles.icon}
          />
        </button>

        <div ref={resultsContainerRef}>
          <DropdownFilterItems
            mapper={mapper}
            isVisible={isActive}
            labelFieldName={labelFieldName}
            fullWidth={fullWidth}
            placeholder={placeholder}
            graphqlQuery={graphqlQuery}
            itemRenderer={itemRenderer}
            actionButton={actionButton}
            onActionButtonClick={() => setIsActive(false)}
            graphqlClient={graphqlClient}
            selectedValue={selectedValue}
            shouldShowInput={shouldShowInput}
            groupBySubfield={groupBySubfield}
            isCustomKeyName={isCustomKeyName}
            clientSideFilter={clientSideFilter}
            isMultipleSelect={isMultipleSelect}
            graphqlVariables={graphqlVariables}
            selectAllOption={selectAllOption}
            wrapperClassName={itemsClassName}
            skipQuery={skipQuery}
            renderFooterSelection={renderFooterSelection}
            onChange={(newValue, data) => {
              onChange(newValue, data);
              !isMultipleSelect && setIsActive(false);
            }}
            optionsWrapperClassName={optionsWrapperClassName}
            customFooterComponent={customFooterComponent}
          />
        </div>
      </div>
    </>
  );
};

export default DropdownFilter;
